import React, { useState, useEffect } from 'react';
import { dispatchService, featureFlagsService } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import DisabledFeature from '../components/common/DisabledFeature';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Truck, 
  Package, 
  MapPin, 
  Clock, 
  Users,
  AlertTriangle,
  RefreshCw,
  Plus,
  Search
} from 'lucide-react';

const DispatchDashboard = () => {
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [loads, setLoads] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    checkFeatureAndLoad();
  }, []);

  const checkFeatureAndLoad = async () => {
    try {
      const enabledFeatures = await featureFlagsService.getEnabledFeatures();
      const isEnabled = enabledFeatures.dispatch_enabled === true;
      setFeatureEnabled(isEnabled);
      
      if (isEnabled) {
        await loadDispatchData();
      }
    } catch (err) {
      console.error('Failed to check feature:', err);
      setFeatureEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  const loadDispatchData = async () => {
    try {
      const [dashboardData, loadsData, dispatchesData, driversData] = await Promise.all([
        dispatchService.getDashboard().catch(() => null),
        dispatchService.getLoads().catch(() => ({ loads: [] })),
        dispatchService.getDispatches().catch(() => ({ dispatches: [] })),
        dispatchService.getAvailableDrivers().catch(() => ({ drivers: [] }))
      ]);
      
      setDashboard(dashboardData);
      setLoads(loadsData.loads || []);
      setDispatches(dispatchesData.dispatches || []);
      setAvailableDrivers(driversData.drivers || []);
    } catch (err) {
      console.error('Failed to load dispatch data:', err);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      available: 'bg-green-100 text-green-800',
      assigned: 'bg-blue-100 text-blue-800',
      en_route_pickup: 'bg-yellow-100 text-yellow-800',
      at_pickup: 'bg-orange-100 text-orange-800',
      loaded: 'bg-purple-100 text-purple-800',
      en_route_delivery: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-teal-100 text-teal-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[status] || 'bg-gray-100'}>{status?.replace(/_/g, ' ').toUpperCase()}</Badge>;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!featureEnabled) {
    return (
      <DashboardLayout>
        <DisabledFeature name="Dispatch Dashboard" requiresAuthorization note="Load & driver-assignment dispatch is built but currently disabled. Configuration is preserved; no operational actions are available." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Dispatch Dashboard</h1>
            <p className="text-gray-600 mt-1">Manage loads and driver assignments</p>
          </div>
          <Button className="flex items-center gap-2">
            <Plus className="w-4 h-4" /> New Load
          </Button>
        </div>

        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Package className="w-8 h-8 text-blue-600" />
                  <div>
                    <div className="text-2xl font-bold">{dashboard.total_available_loads || 0}</div>
                    <div className="text-sm text-gray-500">Available Loads</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Truck className="w-8 h-8 text-green-600" />
                  <div>
                    <div className="text-2xl font-bold">{dashboard.active_dispatches || 0}</div>
                    <div className="text-sm text-gray-500">Active Dispatches</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <MapPin className="w-8 h-8 text-orange-600" />
                  <div>
                    <div className="text-2xl font-bold">{dashboard.todays_pickups || 0}</div>
                    <div className="text-sm text-gray-500">Today&apos;s Pickups</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-purple-600" />
                  <div>
                    <div className="text-2xl font-bold">{dashboard.todays_deliveries || 0}</div>
                    <div className="text-sm text-gray-500">Today&apos;s Deliveries</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Users className="w-8 h-8 text-teal-600" />
                  <div>
                    <div className="text-2xl font-bold">{dashboard.available_drivers || 0}</div>
                    <div className="text-sm text-gray-500">Available Drivers</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b mb-6 overflow-x-auto">
          {['overview', 'loads', 'dispatches', 'drivers'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium capitalize whitespace-nowrap ${
                activeTab === tab 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search loads, drivers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Content */}
        {activeTab === 'loads' && (
          <Card>
            <CardHeader>
              <CardTitle>All Loads</CardTitle>
            </CardHeader>
            <CardContent>
              {loads.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No loads found. Create a new load to get started.
                </div>
              ) : (
                <>
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">Reference</th>
                        <th className="text-left py-3 px-2">Origin</th>
                        <th className="text-left py-3 px-2">Destination</th>
                        <th className="text-left py-3 px-2">Rate</th>
                        <th className="text-left py-3 px-2">Status</th>
                        <th className="text-left py-3 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loads.map(load => (
                        <tr key={load.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-2 font-medium">{load.reference_number}</td>
                          <td className="py-3 px-2">{load.shipper_city}, {load.shipper_state}</td>
                          <td className="py-3 px-2">{load.consignee_city}, {load.consignee_state}</td>
                          <td className="py-3 px-2">${load.rate?.toLocaleString()}</td>
                          <td className="py-3 px-2">{getStatusBadge(load.status)}</td>
                          <td className="py-3 px-2">
                            <Button variant="ghost" size="sm">View</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Mobile cards */}
                <div className="md:hidden space-y-3" data-testid="loads-cards">
                  {loads.map(load => (
                    <div key={load.id} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium break-words">{load.reference_number}</div>
                        {getStatusBadge(load.status)}
                      </div>
                      <div className="mt-2 text-sm text-gray-600 break-words">
                        {load.shipper_city}, {load.shipper_state} → {load.consignee_city}, {load.consignee_state}
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <div className="text-sm font-medium">${load.rate?.toLocaleString()}</div>
                        <Button variant="ghost" size="sm">View</Button>
                      </div>
                    </div>
                  ))}
                </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'dispatches' && (
          <Card>
            <CardHeader>
              <CardTitle>Active Dispatches</CardTitle>
            </CardHeader>
            <CardContent>
              {dispatches.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No active dispatches.
                </div>
              ) : (
                <div className="space-y-4">
                  {dispatches.map(dispatch => (
                    <div key={dispatch.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">{dispatch.load?.reference_number || 'N/A'}</div>
                          <div className="text-sm text-gray-500">
                            {dispatch.load?.shipper_city} → {dispatch.load?.consignee_city}
                          </div>
                        </div>
                        {getStatusBadge(dispatch.status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'drivers' && (
          <Card>
            <CardHeader>
              <CardTitle>Available Drivers</CardTitle>
            </CardHeader>
            <CardContent>
              {availableDrivers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No available drivers.
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {availableDrivers.map(driver => (
                    <div key={driver.id} className="border rounded-lg p-4">
                      <div className="font-medium">{driver.first_name} {driver.last_name}</div>
                      <div className="text-sm text-gray-500">{driver.email}</div>
                      <div className="mt-2">
                        <Badge className={driver.has_active_dispatch ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}>
                          {driver.has_active_dispatch ? 'On Assignment' : 'Available'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'overview' && (
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Recent Loads</CardTitle>
              </CardHeader>
              <CardContent>
                {loads.slice(0, 5).map(load => (
                  <div key={load.id} className="flex justify-between items-center py-2 border-b last:border-0">
                    <div>
                      <div className="font-medium text-sm">{load.reference_number}</div>
                      <div className="text-xs text-gray-500">{load.shipper_city} → {load.consignee_city}</div>
                    </div>
                    {getStatusBadge(load.status)}
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Active Dispatches</CardTitle>
              </CardHeader>
              <CardContent>
                {dispatches.slice(0, 5).map(dispatch => (
                  <div key={dispatch.id} className="flex justify-between items-center py-2 border-b last:border-0">
                    <div>
                      <div className="font-medium text-sm">{dispatch.load?.reference_number || 'N/A'}</div>
                      <div className="text-xs text-gray-500">Driver assigned</div>
                    </div>
                    {getStatusBadge(dispatch.status)}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DispatchDashboard;
